import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Package, Star, Edit, Power, Plus, Loader, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { productsApi } from '../services/api';
import { message } from 'antd';

const ProductDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [addItemData, setAddItemData] = useState({ productId: '', quantity: 1 });
  const [addingItem, setAddingItem] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [updatingQtyId, setUpdatingQtyId] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      try {
        let fullData = null;
        try {
          const prodData = await productsApi.getProductById(id);
          // Standardize response structure for UI if product found
          fullData = {
            ...prodData,
            productType: 'product',
            name: prodData.productName || prodData.name,
            image: prodData.imageUrl || prodData.image,
            mrp: prodData.mrp || prodData.price,
            stock: prodData.stockQuantity || prodData.stock,
            status: prodData.isActive ?? prodData.status,
          };
        } catch (productErr) {
          console.warn('Id not found in products, checking kits...', productErr);
        }

        if (!fullData) {
          try {
            const kitData = await productsApi.getKitById(id);
            // Standardize kit structure for UI
            fullData = {
              ...kitData,
              productType: 'kit',
              name: kitData.kitName || kitData.name,
              image: kitData.kitImageUrl || kitData.image,
              price: kitData.kitPrice || kitData.price,
              mrp: kitData.kitPrice || kitData.mrp,
              stock: 10, // Kits usually don't have stock themselves, or get it from kitData
              status: kitData.isActive ?? kitData.status,
              kitProducts: kitData.kitItems || []
            };
          } catch (kitErr) {
            console.error('Id not found in kits either:', kitErr);
            message.error('Product/Kit not found!');
          }
        }

        setProduct(fullData);
      } catch (err) {
        console.error('Error fetching details:', err);
        message.error('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (id) loadProduct();
  }, [id]);

  const handleEdit = () => {
    localStorage.setItem('editingProduct', JSON.stringify(product));
    navigate('/products/add');
  };

  // API 35 – Add Item to Kit: POST /api/product-kits/:id/items
  const handleAddKitItem = async () => {
    if (!addItemData.productId.trim()) {
      message.warning('Please enter a product ID');
      return;
    }
    setAddingItem(true);
    try {
      const payload = {
        productId: addItemData.productId.trim(),
        quantity: parseInt(addItemData.quantity) || 1
      };
      const res = await productsApi.addKitItem(product.id, payload);
      // Refresh kit data from server
      const updated = await productsApi.getKitById(product.id);
      const kitItems = updated.kitItems || [];
      setProduct(prev => ({
        ...prev,
        kitProducts: kitItems.map(item => ({
          id: item.id || item.productId,
          name: item.productName || item.productId?.productName || 'Product',
          image: item.imageUrl || item.productId?.imageUrl || '',
          quantity: item.quantity || 1,
        }))
      }));
      message.success('Product added to kit successfully!');
      setShowAddItemForm(false);
      setAddItemData({ productId: '', quantity: 1 });
    } catch (err) {
      console.error('Error adding item to kit (API 35):', err);
      message.error(err.message || 'Failed to add item to kit');
    } finally {
      setAddingItem(false);
    }
  };

  // API 36 – Remove Item from Kit: DELETE /api/product-kits/:id/items/:itemId
  const handleRemoveKitItem = async (itemId) => {
    if (!window.confirm('Remove this item from the kit?')) return;
    setRemovingItemId(itemId);
    try {
      await productsApi.removeKitItem(product.id, itemId);
      setProduct(prev => ({
        ...prev,
        kitProducts: (prev.kitProducts || []).filter(p => String(p.id) !== String(itemId))
      }));
      message.success('Item removed from kit');
    } catch (err) {
      console.error('Error removing kit item (API 36):', err);
      message.error(err.message || 'Failed to remove item');
    } finally {
      setRemovingItemId(null);
    }
  };

  // API 37 – Update Item Quantity: PATCH /api/product-kits/:id/items/:itemId/quantity
  const handleUpdateKitItemQty = async (itemId, newQty) => {
    if (newQty < 1) return;
    const prevProducts = product.kitProducts || [];
    // Optimistic update
    setProduct(prev => ({
      ...prev,
      kitProducts: (prev.kitProducts || []).map(p =>
        String(p.id) === String(itemId) ? { ...p, quantity: newQty } : p
      )
    }));
    setUpdatingQtyId(itemId);
    try {
      await productsApi.updateKitItemQuantity(product.id, itemId, newQty);
    } catch (err) {
      // Rollback to previous
      setProduct(prev => ({ ...prev, kitProducts: prevProducts }));
      console.error('Error updating kit item quantity (API 37):', err);
      message.error('Failed to update quantity');
    } finally {
      setUpdatingQtyId(null);
    }
  };

  const handleToggleKitStatus = async () => {
    if (!product || product.productType !== 'kit') return;
    setTogglingStatus(true);
    try {
      const isCurrentlyActive = product.status === true || product.isActive === true;
      if (isCurrentlyActive) {
        // API 34 – Deactivate Kit: PATCH /api/product-kits/:id/deactivate
        await productsApi.deactivateKit(product.id);
        setProduct(prev => ({ ...prev, status: false, isActive: false }));
        message.success('Kit deactivated successfully');
      } else {
        // API 33 – Activate Kit: PATCH /api/product-kits/:id/activate
        await productsApi.activateKit(product.id);
        setProduct(prev => ({ ...prev, status: true, isActive: true }));
        message.success('Kit activated successfully');
      }
    } catch (err) {
      console.error('Error toggling kit status (API 33/34):', err);
      message.error('Failed to update kit status');
    } finally {
      setTogglingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen p-4 desktop:p-8 flex items-center justify-center transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: currentTheme.color }}></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={`min-h-screen p-4 desktop:p-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto text-center py-12">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Product Not Found</h2>
          <button
            onClick={() => navigate('/products/list')}
            className="px-6 py-3 text-white rounded-lg font-medium"
            style={{ backgroundColor: currentTheme.color }}
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 desktop:p-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/products/list')}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-white'}`}
            >
              <ArrowLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-600'}`} />
            </button>
            <div>
              <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Product Details</h1>
              {product.productType === 'kit' && (
                <span className="inline-block mt-1 px-3 py-1 text-white rounded-full text-xs font-medium" style={{ backgroundColor: currentTheme.color }}>
                  Kit/Combo Product
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {product.productType === 'kit' && (
              <button
                onClick={handleToggleKitStatus}
                disabled={togglingStatus}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-colors shadow-lg border-2 ${(product.status === true || product.isActive === true)
                  ? (isDark ? 'border-red-900/50 text-red-400 bg-red-900/20 hover:bg-red-900/30' : 'border-red-400 text-red-600 bg-red-50 hover:bg-red-100')
                  : (isDark ? 'border-green-900/50 text-green-400 bg-green-900/20 hover:bg-green-900/30' : 'border-green-400 text-green-600 bg-green-50 hover:bg-green-100')
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                <Power className="w-5 h-5" />
                {togglingStatus ? 'Updating...' : (product.status === true || product.isActive === true) ? 'Deactivate' : 'Activate'}
              </button>
            )}
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-6 py-3 text-white rounded-lg font-bold transition-colors shadow-lg border-2 border-white"
              style={{ backgroundColor: currentTheme.color }}
            >
              <Edit className="w-5 h-5" />
              Edit Product
            </button>
          </div>
        </div>

        {/* Main Product Info Card */}
        <div className={`rounded-xl p-6 mb-6 border-2 transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'border-[#eeeeee]'}`} 
          style={{ background: isDark ? undefined : `linear-gradient(to bottom right, ${currentTheme.color}1A, #f0fdf4)`, borderColor: isDark ? undefined : `${currentTheme.color}4D` }}>
          <div className="flex gap-6">
            {/* Main Product/Combo Image */}
            <div className="flex-shrink-0">
              <img
                src={product.image}
                alt={product.name}
                className="w-40 h-40 rounded-xl object-cover border-2 border-white shadow-lg"
              />
              <p className="text-xs text-center font-medium mt-2" style={{ color: currentTheme.color }}>
                {product.productType === 'kit' ? 'Main Kit Image' : 'Product Image'}
              </p>
            </div>

            {/* Basic Info */}
            <div className="flex-1">
              <h4 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.name || product.title}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className={`${isDark ? 'bg-white/5' : 'bg-white shadow-sm'} rounded-lg p-3`}>
                  <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Category</p>
                  <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.category}</p>
                </div>
                <div className={`${isDark ? 'bg-white/5' : 'bg-white shadow-sm'} rounded-lg p-3`}>
                  <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sub Category</p>
                  <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.subCategory || 'N/A'}</p>
                </div>
                <div className={`${isDark ? 'bg-white/5' : 'bg-white shadow-sm'} rounded-lg p-3`}>
                  <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Price (MRP)</p>
                  <p className="text-xl font-bold" style={{ color: currentTheme.color }}>₹ {product.price || product.mrp}</p>
                </div>
                <div className={`${isDark ? 'bg-white/5' : 'bg-white shadow-sm'} rounded-lg p-3`}>
                  <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Package Size</p>
                  <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.packageSize || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kit Products Section */}
        {product.productType === 'kit' && product.kitProducts && product.kitProducts.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h5 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Package className="w-6 h-6" style={{ color: currentTheme.color }} />
                Products in this Kit
              </h5>
              <div className="flex items-center gap-3">
                <div className="px-4 py-2 rounded-full font-semibold" style={{ backgroundColor: `${currentTheme.color}26`, color: currentTheme.color }}>
                  {product.kitProducts.length} Products
                </div>
                <button
                  onClick={() => { setShowAddItemForm(prev => !prev); }}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-bold transition-colors shadow-sm border-2 border-white"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>
            </div>

            {/* Add Item Form – API 35 */}
            {showAddItemForm && (
              <div className="mb-4 rounded-xl p-4 flex flex-wrap gap-3 items-end border-2 transition-all" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : `${currentTheme.color}0D`, borderColor: isDark ? 'rgba(255,255,255,0.1)' : `${currentTheme.color}33` }}>
                <div className="flex-1 min-w-[180px]">
                  <label className={`text-xs font-bold mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Product ID *</label>
                  <input
                    type="text"
                    placeholder="Enter product ID"
                    value={addItemData.productId}
                    onChange={e => setAddItemData(prev => ({ ...prev, productId: e.target.value }))}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm transition-all ${isDark ? 'bg-[#0f172a] text-white border-white/10' : ''}`}
                    style={{ borderColor: isDark ? undefined : `${currentTheme.color}4D` }}
                  />
                </div>
                <div className="w-28">
                  <label className={`text-xs font-bold mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Quantity *</label>
                  <input
                    type="number"
                    min="1"
                    value={addItemData.quantity}
                    onChange={e => setAddItemData(prev => ({ ...prev, quantity: e.target.value }))}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm transition-all ${isDark ? 'bg-[#0f172a] text-white border-white/10' : ''}`}
                    style={{ borderColor: isDark ? undefined : `${currentTheme.color}4D` }}
                  />
                </div>
                <button
                  onClick={handleAddKitItem}
                  disabled={addingItem}
                  className="flex items-center gap-2 px-5 py-2 text-white rounded-lg font-bold disabled:opacity-60 transition-colors border-2 border-white"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  {addingItem ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {addingItem ? 'Adding...' : 'Confirm Add'}
                </button>
                <button
                  onClick={() => { setShowAddItemForm(false); setAddItemData({ productId: '', quantity: 1 }); }}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${isDark ? 'text-gray-400 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.kitProducts.map((kitProduct, index) => (
                <div key={kitProduct.id} className={`rounded-xl p-4 border-2 transition-all shadow-sm ${isDark ? 'bg-[#1e293b] border-white/5 hover:border-white/10' : 'bg-white border-gray-100 hover:border-gray-200'}`}>
                  {/* Product Number Badge */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-md flex-shrink-0" style={{ backgroundColor: currentTheme.color }}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h6 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{kitProduct.name || 'Unnamed Product'}</h6>
                    </div>
                    {/* Remove Item – API 36 */}
                    <button
                      onClick={() => handleRemoveKitItem(kitProduct.id)}
                      disabled={removingItemId === kitProduct.id}
                      title="Remove from kit"
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {removingItemId === kitProduct.id
                        ? <Loader className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Product Image */}
                  {kitProduct.image && (
                    <div className="mb-3">
                      <img
                        src={kitProduct.image}
                        alt={kitProduct.name}
                        className={`w-full h-32 rounded-lg object-cover border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
                      />
                    </div>
                  )}

                  {/* Product Details */}
                  <div className="space-y-2">
                    {/* Quantity – with API 37 inline controls */}
                    <div className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : `${currentTheme.color}1A` }}>
                      <span className={`text-sm font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Quantity:</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateKitItemQty(kitProduct.id, (kitProduct.quantity || 1) - 1)}
                          disabled={kitProduct.quantity <= 1 || updatingQtyId === kitProduct.id}
                          className={`w-7 h-7 rounded-full border-2 flex items-center justify-center font-bold hover:bg-opacity-80 disabled:opacity-40 transition-colors ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white'}`}
                          style={{ borderColor: isDark ? undefined : `${currentTheme.color}4D`, color: isDark ? undefined : currentTheme.color }}
                        >
                          −
                        </button>
                        <span className="text-lg font-bold w-8 text-center" style={{ color: currentTheme.color }}>
                          {updatingQtyId === kitProduct.id
                            ? <Loader className="w-4 h-4 animate-spin mx-auto" />
                            : kitProduct.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateKitItemQty(kitProduct.id, (kitProduct.quantity || 1) + 1)}
                          disabled={updatingQtyId === kitProduct.id}
                          className={`w-7 h-7 rounded-full border-2 flex items-center justify-center font-bold hover:bg-opacity-80 disabled:opacity-40 transition-colors ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white'}`}
                          style={{ borderColor: isDark ? undefined : `${currentTheme.color}4D`, color: isDark ? undefined : currentTheme.color }}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {kitProduct.altText && (
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <p className={`text-xs font-bold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Alt Text:</p>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{kitProduct.altText}</p>
                      </div>
                    )}

                    {kitProduct.range && (
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <p className={`text-xs font-bold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Range:</p>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{kitProduct.range}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Kit Summary Card */}
            <div className="mt-6 p-5 rounded-xl shadow-lg" style={{ background: `linear-gradient(to right, ${currentTheme.color}, #15803d)` }}>
              <div className="flex items-center justify-between text-white">
                <div>
                  <p className="text-sm opacity-90 mb-1">Total Items in Kit</p>
                  <p className="text-4xl font-bold">
                    {product.kitProducts.reduce((sum, p) => sum + parseInt(p.quantity || 0), 0)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-90 mb-1">Total Products</p>
                  <p className="text-4xl font-bold">{product.kitProducts.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Content Section */}
        {(product.aboutProduct || product.description || (product.tags && product.tags.length > 0)) && (
          <div className={`mb-6 rounded-xl p-5 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
            <h5 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Product Content</h5>

            {product.aboutProduct && (
              <div className="mb-4">
                <p className={`text-sm font-bold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>About Product</p>
                <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>{product.aboutProduct}</p>
              </div>
            )}

            {product.ratings > 0 && (
              <div className="mb-4">
                <p className={`text-sm font-bold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Ratings</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${star <= product.ratings ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="mb-4">
                <p className={`text-sm font-bold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Related Tags</p>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, index) => (
                    <span key={index} className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? 'bg-white/5 text-teal-400 border border-teal-900/30' : 'bg-teal-100 text-teal-700'}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {product.description && (
              <div>
                <p className={`text-sm font-bold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Description</p>
                <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{product.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Product Benefits Section */}
        {((product.attributes && product.attributes.length > 0) || (product.ingredients && product.ingredients.length > 0)) && (
          <div className={`mb-6 rounded-xl p-5 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
            <h5 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Product Benefits</h5>

            {product.attributes && product.attributes.length > 0 && (
              <div className="mb-4">
                <p className={`text-sm font-bold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Attributes</p>
                <div className="flex flex-wrap gap-2">
                  {product.attributes.map((attr, index) => (
                    <span key={index} className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? 'bg-white/5 text-green-400 border border-green-900/30' : 'bg-green-100 text-green-700'}`}>
                      {attr}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {product.ingredients && product.ingredients.length > 0 && (
              <div>
                <p className={`text-sm font-bold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Ingredients</p>
                <div className="space-y-2">
                  {product.ingredients.map((ingredient, index) => (
                    <div key={index} className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{ingredient.heading}</p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{ingredient.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pricing Section */}
        <div className={`mb-6 rounded-xl p-5 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
          <h5 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Pricing Details</h5>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>MRP</p>
              <p className="text-lg font-bold" style={{ color: currentTheme.color }}>₹ {product.mrp || product.price}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Batch Number</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.batchNumber || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>HSN Code</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.hsnCode || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Tax</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.tax || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Manufacturing Date</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.manufactoryDate || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Expiry Date</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.expiryDate || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>PTS</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.pts || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>PTR</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.ptr || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>PTD</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.ptd || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>B2B Discount</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.b2bDiscount || 'N/A'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>B2C Discount</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.b2cDiscount || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        {product.faqs && product.faqs.length > 0 && (
          <div className={`mb-6 rounded-xl p-5 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
            <h5 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>FAQ</h5>
            <div className="space-y-3">
              {product.faqs.map((faq, index) => (
                <div key={index} className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <p className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{faq.question}</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stock & Status Section */}
        <div className={`rounded-xl p-5 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
          <h5 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Stock & Status</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Units</p>
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {product.productType === 'kit' && product.kitProducts
                  ? product.kitProducts.reduce((sum, p) => sum + parseInt(p.quantity || 0), 0)
                  : product.totalUnits || '0'}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Stock Status</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.stockStatus || 'In Stock'}</p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Status</p>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.status ? 'Active' : 'Inactive'}</p>
            </div>
            {product.url && (
              <div className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Product URL</p>
                <a href={product.url} target="_blank" rel="noopener noreferrer" className="text-sm text-teal-600 hover:underline truncate block">
                  View Link
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
